/**
 * Form Control
 *
 * @description Component wrapper handling input changes and validation
 * of fields as well as global validation
 *
 * @implementation
 * withFormControl(config, true)(YourComponent);
 *
 * @config = {
 *   password: {
 *     required: true,
 *     rules: [
 *       'password',
 *       (value) => ({
 *         valid: value.includes('super'),
 *         'data-error': 'superSecuredPassword'
 *       })
 *     ],
 *     value: 'testpassword'
 *   }
 * }
 *
 * @param {boolean} required [optional]
 * specifies if the field will be used for global validation
 * when presented with a value, single field validation will apply
 *
 * @param {any} value [optional]
 * set this property if you wish to initially set the value of a field
 *
 * @param {(string | function)[]} rules [optional]
 * represents your validation rules for a field
 *
 * [***HINT***]
 * If you wish to use generic validators, located in ./rules.ts,
 * pass the matching rule name to the array of rules
 * [***HINT***]
 *
 */
import React, { ComponentType } from 'react';
import { RULES } from './Rules';
import { NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  BlurEvent
} from '@progress/kendo-react-dropdowns/dist/es/common/events';
import {
  ABSTRACT_BASE_FORM_PROPS,
  FORM_CONTROL,
  FORM_CONTROL_CONFIG,
  FORM_CONTROL_STATE,
  FORM_CONTROL_STATE_DATA, 
  FORM_GET_FIELDS
} from './Types';
import * as _ from 'lodash';

function withFormControl<WRAPPED_COMPONENT_PROPS>(config: FORM_CONTROL_CONFIG, initialValidation: boolean = false) {
  type WRAPPED_COMPONENT = ABSTRACT_BASE_FORM_PROPS & WRAPPED_COMPONENT_PROPS
  return function (WrappedComponent: ComponentType<WRAPPED_COMPONENT>) {
    return class extends React.Component<WRAPPED_COMPONENT, FORM_CONTROL_STATE> {
      constructor(props: WRAPPED_COMPONENT) {
        super(props);

        this.operationConfig = _.cloneDeep(config);
        this.initialSetup();
      }

      operationConfig: FORM_CONTROL_CONFIG = {};
      childRef: any = React.createRef();

      /**
       * Names and valid of all fields with required=true or rules
       * e.g. {
       *   firstName: true,
       *   lastName: false
       * }
       */
      allValidation: object = {}

      /**
       * Initial form setup
       * Create data and validation rules
       */
      initialSetup = () => {
        // eslint-disable-next-line react/no-direct-mutation-state
        this.state = {
          data: this.generateInitialData(),
          dynamicRules: {},
          valid: false,
          ignoredFields: [], // ignores any custom rules
          isAllValid: this.generateInitialIsAllValid(config)
        };
      }

      componentDidMount(): void {
        if (initialValidation) {
          this.runFullValidation();
        }
      }

      /**
       * Return new value for isAllValid
       * @param {object} data
       * @param {boolean} isSetData
       */
      handleAllValid = (data: object, isSetData?: boolean) => {
        const allValid = {};

        for (let field in data) {
          const { required, value, valid, rules, hasDynamicRule } = data[field];

          if (required || rules || hasDynamicRule || (isSetData && this.allValidation.hasOwnProperty(field))) {
            allValid[field] = this.validateField(field, value).valid && valid;
          }
        }
        return allValid;
      }

      /**
       * Generate initial state isAllValid
       * isAllValid represents are all fields with required=true or rules valid
       * @param {object} data
       */
      generateInitialIsAllValid = (data: object) => {
        const allValid = this.handleAllValid(data);
        this.allValidation = allValid;
        return this.checkIsAllFieldValid(allValid);
      }

      /**
       * Remove all fields that are not in init configuration, and keeps values of once that were in init configuration.
       * @param {boolean} removeIgnoredFields
       */
      removeAdditionallyAddedFields = (removeIgnoredFields: boolean = true) => {
        const newData = _.pick(this.state.data, Object.keys(config));
        this.operationConfig = _.cloneDeep(config);

        this.setState({
          ...this.state,
          ...(removeIgnoredFields && { ignoredFields: [] }),
          data: newData,
          valid: this.validateAllFields(newData),
          isAllValid: this.generateInitialIsAllValid(config)
        });
      };

      /**
       * Restart isAllValid value in state
       */
      restartAllValid = () => {
        const allValidData = { ...this.allValidation };
        const allValid = {};

        for (let name in allValidData) {
          allValid[name] = false;
        }

        this.allValidation = allValid;
        this.setState({ 
          isAllValid: this.generateInitialIsAllValid(this.operationConfig) 
        });
      }

      /**
       * Return are all fields with required=true or rules valid
       * @param {object} allValidData
       */
      checkIsAllFieldValid = (allValidData: object) => {
        if (_.isEmpty(allValidData)) {
          return true;
        } else {
          const allValid = Object.values(allValidData);
          return allValid.every(item => item === true);
        }
      }

      /**
       * Return true when the value is falsy (depending on a type)
       */
      checkForFalsyValues = (value: any): boolean => {
        let invalid: boolean = false;
        switch (typeof value) {
          case 'number':
            invalid = _.isNull(value);
            break;
          case 'string':
            invalid = !value.length || value === 'rgba(0, 0, 0, 0)';
            break;
          case 'boolean':
            invalid = !value;
            break;
          case 'object':
            if (value instanceof Date) {
              invalid = false;
            } else {
              invalid = _.isEmpty(value) || _.isNull(value);
            }
            break;
          default:
            invalid = _.isUndefined(value);
            break;
        }

        return invalid;
      };

      /**
       * Generate initial state data
       */
      generateInitialData = (customFields?: any): Record<string, FORM_CONTROL_STATE_DATA> => {
        let data: Record<string, FORM_CONTROL_STATE_DATA> = {};
        const source = customFields || this.operationConfig;
        for (let prop in source) {
          const fieldState = source[prop];
          const hasValue = fieldState.hasOwnProperty('value');

          data[prop] = {
            value: hasValue ? fieldState.value : ''
          };

          if (fieldState.hasOwnProperty('valid')) {
            data[prop].valid = fieldState.valid;
          }

          if (fieldState.hasOwnProperty('disabled')) {
            data[prop].disabled = fieldState.disabled;
          }
        }

        return data;
      };

      /**
       * Return fields all configuration object
       */
      getAllConfigField = ():{ [key: string]:FORM_CONTROL_STATE_DATA} => this.state.data

      /**
       * Return fields configuration object
       */
      getFields = (): FORM_GET_FIELDS => {
        let data = _.cloneDeep(this.state.data);

        for (let field in data) {
          data[field] = _.omit(this.state.data[field], ['data-error', 'rules', 'hasDynamicRule', 'additionalRulesConfig']);
        }

        return data;
      };

      /**
       * Returns raw form data e.g. { [key]: value }
       */
      getData = (keys?: string | string[]): FORM_CONTROL_STATE_DATA => {
        const formData: Record<string, FORM_CONTROL_STATE_DATA> = {};

        if (_.isUndefined(keys)) {
          const { data } = this.state;
          for (let prop in data) {
            if (data.hasOwnProperty(prop)) {
              formData[prop] = this.getValueByKey(prop);
            }
          }
        } else if (_.isString(keys)) {
          formData[keys] = this.getValueByKey(keys);
        } else if (_.isArray(keys)) {
          keys.forEach((key) => {
            formData[key] = this.getValueByKey(key);
          });
        }

        return formData;
      };

      /**
       * Find and return param from event
       * Deps supported: target, target.component, target.element
       * @param {NumericTextBoxChangeEvent | DatePickerChangeEvent | MultiSelectChangeEvent | any} e
       */
      getParamFromEvent = (e: NumericTextBoxChangeEvent | DatePickerChangeEvent | MultiSelectChangeEvent | any) => {
        let value: any;
        let name;

        if (e.target?.element?.attributes?.type?.nodeValue === 'checkbox' || e.target?.element?.attributes?.type?.nodeValue === 'radio') {
          value = e.value;
          name = e.target?.element?.name;
        } else if (e.target?.element?.current?.localName === 'textarea') {
          value = e.target?.element?.current?.value;
          name = e.target?.element?.current?.name;
        } else if (e.target?.constructor.name === 'PuzzleTagField') {
          name = e.target?.name || e.target?.component?.name || e.target?.props?.name;
          value = !_.isUndefined(e?.target?.value) ? e?.target?.value : e?.value;
        } else {
          name = e.name || e.target?.name || e.target?.component?.name || e.target?.props?.name;
          if (e.target?.props?.valueAsSimpleType) {
            value = !_.isUndefined(e?.target?.value) ? e?.target?.value : e?.value;
          } else {
            if (e.detail) {
              name = e.detail.name;
              value = e.detail.value;
            } else {
              value = !_.isUndefined(e?.value) ? e?.value : e?.target?.value;
            }
          }
        }

        return {
          value,
          name
        };
      }

      /**
       * Returns raw form data value by key.
       */
      getValueByKey = (key: string): any => this.state.data[key].value;

      /**
       * On blur delete the value of an invalid field if it's not required
       * @param {BlurEvent | any } e
       * @param {Function} afterChangeHandler Form field Function
       */
      handleBlur = (e: BlurEvent<any> | any, afterChangeHandler?: Function) => {
        let { value, name } = this.getParamFromEvent(e);

        if (name) {
          let event = { ...e };
          let descriptorValueProperty;
          if (e.target?.element?.current?.localName === 'textarea') {
            descriptorValueProperty = !!Object.getOwnPropertyDescriptor(event.target.element.current, 'value');
          } else {
            descriptorValueProperty = event.target && !!Object.getOwnPropertyDescriptor(event.target, 'value');
          }
          if (value && value.trim && descriptorValueProperty && descriptorValueProperty['set']) {
            value = value.trim();
          }

          const validity: FORM_CONTROL_STATE_DATA = this.validateField(name, value);

          this.handleSetState(name, value, validity, afterChangeHandler);
        }
      };

      /**
       * Update the form data when input's value changes
       * @param {NumericTextBoxChangeEvent | DatePickerChangeEvent | MultiSelectChangeEvent | any} e
       * @param {Function} afterChangeHandler Form field Function
       * @param ignoreChangeValue
       */
      handleChange = (e: NumericTextBoxChangeEvent | DatePickerChangeEvent | MultiSelectChangeEvent | any, afterChangeHandler?: Function, ignoreChangeValue?: boolean) => {
        let { value, name } = this.getParamFromEvent(e);

        if (name) {
          const validity: FORM_CONTROL_STATE_DATA = this.validateField(name, value);
          this.handleSetState(name, value, validity, afterChangeHandler, ignoreChangeValue);
        }
      };

      /**
       * Update setState
       * @param {String} name Form field name
       * @param {String/Number/Object/Boolean} value Form field value
       * @param {Object} validity Form field Object that describes validation
       * @param {Function} afterChangeHandler Form field Function
       * @param ignoreChangeValue
       */
      handleSetState = (name: string, value: any, validity: object, afterChangeHandler?: Function, ignoreChangeValue?: boolean) => {
        if (typeof this.allValidation[name] === 'boolean') {
          const valid = { ...validity };
          this.allValidation = {
            ...this.allValidation,
            [name]: valid['valid']
          };
        }

        this.setState((prevState: any) => ({
          ...prevState,
          data: {
            ...prevState.data,
            // @ts-ignore
            [name]: {
              value: ignoreChangeValue ? prevState.data[name]?.value : value,
              ...validity
            }
          },
          isAllValid: this.checkIsAllFieldValid(this.allValidation)
        }), () => this.runShallowValidation(afterChangeHandler));
      }

      ignoreFields = (ignoredFields: string[] = []): void => {
        const ignoredFieldsChanged = !_.isEqual(_.sortBy(this.state.ignoredFields), _.sortBy(ignoredFields));
        if (ignoredFieldsChanged) {
          this.setState({ ignoredFields }, () => this.runFullValidation());
        }
      };

      /**
       * Return all custom rules if present
       */
      mapRulesForValidation = (rule: any): Function[] => {
        const isGenericRule = typeof rule === 'string';

        if (isGenericRule) {
          const hasRule: boolean = RULES.hasOwnProperty(rule);
          if (!hasRule) {
            throw new Error(`FORM CONTROL - "${rule}" is not a generic validation rule! Check the form control configuration.`);
          }
          rule = [...RULES[rule]];
        }

        return rule;
      };

      clearAll = (afterClearAllHandler?: Function, defaultData: any = {}, customFields?: any) => {
        let data: Record<string, FORM_CONTROL_STATE_DATA> = {};
        const source = customFields || this.operationConfig;
        for (let prop in source) {
          const fieldState = source[prop];
          if (fieldState.type === 'daterange') {
            data[prop] = {
              value: {
                start: null,
                end: null
              }
            };
          } else if (fieldState.type === 'multiselect') {
            data[prop] = {
              value: []
            };
          } else {
            data[prop] = {
              value: ''
            };
          }
          if (fieldState.hasOwnProperty('valid')) {
            data[prop].valid = fieldState.valid;
          }
          if (fieldState.hasOwnProperty('disabled')) {
            data[prop].disabled = fieldState.disabled;
          }
        }
        this.setState({
          data: { ...data, ...defaultData }
        }, () => {
          if (_.isFunction(afterClearAllHandler)) {
            afterClearAllHandler();
          }
        });
      };

      reset = (afterResetHandler?: Function) => {
        const data = this.generateInitialData();
        this.setState({ data }, () => {
          if (_.isFunction(afterResetHandler)) {
            afterResetHandler();
          }
        });

        this.restartAllValid();
      };

      /**
       * Validate each field's rules and update global validity state
       */
      runFullValidation = (staticFields?: string[]): boolean => {
        const currentData = this.state.data;
        let validatedData: Record<string, FORM_CONTROL_STATE_DATA> = {};

        for (let prop in currentData) {
          const value = currentData[prop].value;
          let validity = {};

          // skip validation for dynamic/custom fields
          if (!staticFields || staticFields.includes(prop)) {
            validity = this.validateField(prop, value);
            validatedData[prop] = {
              ...currentData[prop],
              ...validity
            };
          } else {
            validatedData[prop] = { value: currentData[prop].value };
          }
        }

        const valid = this.validateAllFields(validatedData);
        this.setState({ data: validatedData, valid });
        setTimeout(this.scrollToElTimeout.bind(this), 0); // set 0ms timeout, just to move call to end of js stack
        return valid;
      };

      /**
       * Update global validity state with validation values from the state
       */
      runShallowValidation = (afterChangeHandler?: Function) => {
        const valid = this.validateAllFields();
        this.setState({ valid }, () => {
          afterChangeHandler && afterChangeHandler();
        });
      };

      /**
       * Scroll to first invalid element, also try to focus element
       */
      scrollToElTimeout = () => {
        const firstInvalidField = document.querySelector('.k-state-invalid');

        if (firstInvalidField) {
          let inputElement: any = firstInvalidField;
          if (!(inputElement instanceof HTMLInputElement)) {
            inputElement = inputElement.querySelector('input');
          }

          if (inputElement) {
            inputElement.focus();
          }

          firstInvalidField.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      };

      /**
       * Update each field's values
       */
      setData = (data: Record<string, FORM_CONTROL_STATE_DATA>, dynamicRules?: any, afterChangeHandler?: Function) => {
        this.setState((prevState: any) => (this.readjustData(data, prevState, dynamicRules)), () => this.runShallowValidation(afterChangeHandler));
      };

      /**
       * Updates the value of all fealds given in data prop.
       * @param data
       * @param prevState
       * @param dynamicRules
       */
      readjustData = (data: Record<string, FORM_CONTROL_STATE_DATA>, prevState: any, dynamicRules?: any) => {
        const dataPropNames = [
          'aria-describedby',
          'ariaDescribedBy',
          'aria-invalid',
          'data-commonnamespace',
          'disabled',
          'data-error',
          'hidden',
          'required',
          'rules',
          'value',
          'valid',
          'hasDynamicRule'
        ];
        let newData = { ...prevState.data };
        let isPropObject;
        let validity: any;
        for (let prop in data) {
          isPropObject = dataPropNames.find(item => !_.isUndefined(_.get(data[prop], item)));
          if (data.hasOwnProperty(prop)) {
            if (isPropObject) {
              data[prop] = { ...newData[prop], ...data[prop] };
              validity = this.validateField(prop, data[prop]?.value, dynamicRules);
            } else {
              validity = this.validateField(prop, data[prop], dynamicRules);
            }
            if (newData[prop]) {
              if (isPropObject) {
                newData[prop] = {
                  ...data[prop],
                  ...validity
                };
              } else {
                newData[prop] = {
                  ...validity,
                  value: data[prop]
                };
              }
            }
          }
        }

        const allValid = this.handleAllValid(newData, true);
        this.allValidation = allValid;

        return {
          ...prevState,
          data: newData,
          isAllValid: this.checkIsAllFieldValid(allValid),
          ...(dynamicRules && { dynamicRules })
        };
      };

      /**
       * Sets dynamic rules
       * @param {Record<string, Function[]>} dynamicRules
       */
      setDynamicRules = (dynamicRules: Record<string, Function[]>) => {
        this.setState({
          dynamicRules
        });
      }

      /**
       * Dynamically set fields if config cannot be provided beforehand (e.g. comes from an API)
       */
      setFields = (fields: any, afterChangeHandler?: Function) => {
        this.operationConfig = { ...this.operationConfig, ...fields };
        const data = this.generateInitialData(fields);
        const allValid = this.handleAllValid({
          ...this.state.data,
          ...data
        }, true);
        this.allValidation = { ...this.allValidation, ...allValid };

        this.setState((prevState: FORM_CONTROL_STATE) => ({
          ...prevState,
          data: {
            ...prevState.data,
            ...data
          },
          isAllValid: this.checkIsAllFieldValid(allValid)
        }), () => this.runShallowValidation(afterChangeHandler));
      };

      /**
       * Shallow validate all fields and return global valid property
       */
      validateAllFields = (data?: FORM_CONTROL_STATE_DATA): boolean => {
        let fields;
        if (data) {
          fields = Object.values(data);
        } else {
          fields = Object.values(this.state.data);
        }
        return !fields.some((field: any) => field.valid === false);
      };

      /**
       * Validate a single field
       */
      validateField = (name: string, value: any, dynamicRules?: any): FORM_CONTROL_STATE_DATA => {
        let { rules, required, hasDynamicRule, additionalRulesConfig } = this.operationConfig[name];
        let validity: FORM_CONTROL_STATE_DATA = {
          valid: true,
          'data-error': undefined
        };
        const isFalsyValue = this.checkForFalsyValues(value);

        // Added for purposes of validating dynamicRules
        let additionalRules = dynamicRules ? dynamicRules[name]?.rules : this.state?.dynamicRules[name]?.rules;
        if (hasDynamicRule) {
          rules = rules ? rules.concat(additionalRules) : additionalRules;
        }

        const ignoredField: boolean = this.state?.ignoredFields.includes(name);
        if (!ignoredField) {
          if (rules && !!value) {
            rules = rules.map(this.mapRulesForValidation).flat();
            let errors: any = rules.filter((rule: any) => !rule(value, additionalRulesConfig).valid);
            if (errors.length) {
              validity = errors[0](value, additionalRulesConfig);
            }
          } else if (required && isFalsyValue) {
            validity = {
              'data-commonnamespace': true,
              'data-error': 'validationErrorMessages.fieldRequired',
              'aria-invalid': true,
              'aria-describedby': `error_${name}`,
              ariaDescribedBy: `error_${name}`,
              valid: false
            };
          }

          if (validity?.valid === false) {
            const errorTxt = this.childRef?.current?.translateErrorMessage(validity);
            validity['errorTxt'] = errorTxt;
          } else {
            validity['errorTxt'] = undefined;
          }
        }

        return validity;
      };

      /**
       * API exposed to the WrappedComponent
       * accessed via this.props.form....
       */
      FORM: FORM_CONTROL = {
        generateInitialData: this.generateInitialData,
        getData: this.getData,
        getFields: this.getFields,
        getAllConfigField: this.getAllConfigField,
        handleBlur: this.handleBlur,
        handleChange: this.handleChange,
        ignoreFields: this.ignoreFields,
        isValid: () => this.state.valid,
        isAllFieldValid: () => this.state.isAllValid, // Returns are all required fields valid. Use for initial validation. If some of the fields are required={true}, valid={true} and value={''}, isAllFieldValid method will return false.
        reset: this.reset,
        clearAll: this.clearAll,
        resetToInitialState: this.initialSetup,
        runFullValidation: this.runFullValidation,
        removeAdditionallyAddedFields: this.removeAdditionallyAddedFields,
        setFields: this.setFields,
        setData: this.setData,
        setDynamicRules: this.setDynamicRules,
        validateField: this.validateField
      };

      render() {
        return (
          <WrappedComponent
            {...this.props as WRAPPED_COMPONENT_PROPS}
            form={this.FORM}
            ref={this.childRef}
          />);
      }
    };
  };
}

export default withFormControl;
